import { useState } from 'react';
import { createPortal } from 'react-dom';
import { LEVEL_LABELS } from '../constants';
import { sortRolesByLevel, migrateInstruments, splitRoles, normalizeInstrument, INSTRUMENT_ORDER } from '../helpers';
import FotoIntera from './FotoIntera';

// Ruoli di un musicista su un brano, nei nomi ufficiali e nell'ordine di sempre.
function ruoliSulBrano(role) {
  const r = [...new Set(splitRoles(role || '').map(normalizeInstrument).filter(Boolean))];
  return r.sort((a, b) => INSTRUMENT_ORDER.indexOf(a) - INSTRUMENT_ORDER.indexOf(b)).join(', ');
}

// Scheda del musicista. Con `tracks` (i brani della serata) e' la scheda di quella
// serata: in cima i brani su cui si e' segnato e cosa suona. Senza, solo il profilo.
// `azioni`: comandi in piu' che la pagina mette in fondo (es. nominare un co-organizzatore).
function MusicianPopup({ musician, onClose, tracks, azioni }) {
  if (!musician) return null;
  const levels = musician.levels || {};
  const allRoles = sortRolesByLevel(migrateInstruments(musician.instruments||[]), musician.sings, levels);
  const [fullscreen, setFullscreen] = useState(false);

  // Nel body: aperto dalla vista serata, che ha un transform, il
  // position:fixed si aggancerebbe alla pagina e non allo schermo.
  return createPortal(
    <>
    {fullscreen && <FotoIntera src={musician.avatar_url} onClose={() => setFullscreen(false)}/>}
    {/* Sopra le finestre (zIndex 9999): si apre anche dalla scheda del brano, che resta aperta sotto. */}
    <div className="popup-overlay" style={{zIndex:10000}} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="popup">
        {/* Header */}
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16}}>
          {musician.avatar_url
            ? <img src={musician.avatar_url} alt="" onClick={()=>setFullscreen(true)}
                style={{width:68,height:68,borderRadius:'50%',objectFit:'cover',flexShrink:0,cursor:'pointer'}}/>
            : <div className="avatar" style={{width:68,height:68,fontSize:24,flexShrink:0}}>{musician.name.charAt(0)}</div>
          }
          <div>
            <div style={{fontWeight:800,fontSize:20,color:'var(--text)'}}>{musician.name}</div>
            {musician.city && <div style={{fontSize:16,color:'var(--muted)',marginTop:2}}>{musician.city}</div>}
          </div>
        </div>

        {/* I suoi brani in questa serata */}
        {tracks && (() => {
          const suoi = tracks
            .filter(t => t.type !== 'free')
            .map(t => ({ t, p: (t.track_players || []).find(p => p.musician_id === musician.id) }))
            .filter(x => x.p);
          return (
            <div style={{marginBottom:16}}>
              <div style={{fontSize:16,fontWeight:700,color:'var(--muted)',letterSpacing:'1px',marginBottom:4}}>
                {suoi.length === 0 ? 'IN QUESTA SERATA' : `SUONA IN ${suoi.length} ${suoi.length === 1 ? 'BRANO' : 'BRANI'}`}
              </div>
              {suoi.length === 0
                ? <div style={{fontSize:17,color:'var(--muted)',padding:'6px 0'}}>Non si è ancora segnato su nessun brano.</div>
                : <div style={{maxHeight:'38vh',overflowY:'auto'}}>
                    {suoi.map(({ t, p }) => (
                      <div key={t.id} style={{padding:'8px 0',borderBottom:'1px solid var(--border)'}}>
                        <div style={{fontSize:17,fontWeight:700,color:'var(--text)',lineHeight:1.25}}>
                          {t.title}
                          {t.played && <span style={{fontSize:16,fontWeight:500,color:'var(--muted)'}}> · suonato</span>}
                        </div>
                        <div style={{fontSize:16,color:'var(--muted)',marginTop:2,lineHeight:1.3}}>
                          {[ruoliSulBrano(p.role), t.artist].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                    ))}
                  </div>}
            </div>
          );
        })()}

        {/* Ruoli */}
        {allRoles.length > 0 && (
          <div style={{marginBottom:14}}>
            {tracks && <div style={{fontSize:16,fontWeight:700,color:'var(--muted)',letterSpacing:'1px',marginBottom:4}}>STRUMENTI</div>}
            {allRoles.map(r => (
              <div key={r} style={{display:'flex',alignItems:'center',justifyContent:'space-between',
                padding:'8px 0',borderBottom:'1px solid var(--border)'}}>
                <span style={{fontSize:17,fontWeight:600,color:'var(--text)'}}>
                  {r}
                </span>
                <span style={{fontSize:16,color:'var(--muted)'}}>
                  {levels[r] > 0 ? LEVEL_LABELS[levels[r]] : '—'}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Contatti */}
        <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:16}}>
          {musician.phone && <a href={`tel:${musician.phone}`} style={{fontSize:17,color:'var(--text)'}}>{musician.phone}</a>}
          {musician.whatsapp_link && (
            <a href={musician.whatsapp_link} target="_blank" rel="noreferrer"
              style={{fontSize:17,color:'var(--text)'}}>WhatsApp</a>
          )}
        </div>

        {azioni}

        <button className="btn btn-secondary" style={{width:'100%',justifyContent:'center'}} onClick={onClose}>
          Chiudi
        </button>
      </div>
    </div>
    </>,
    document.body
  );
}

export default MusicianPopup;
